import {useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {AlertCircle, FileBarChart2, FileSpreadsheet, Filter, SearchX} from 'lucide-react'
import {PageTitle} from '../../../_metronic/layout/core'
import * as API from '../../api'
import BtnHelp from '../../components/BtnHelp'
import {isSuperAdmin} from '../../modules/auth'
import {ReportType} from '../../types/report-type'
import ManualModal from '../utils/ManualModal'
import ViewerModal from '../utils/ViewerModal'
import DownloadModal from './DownloadModal'
import ReportFiltersModal from './ReportFiltersModal'
import {requestReport} from './shared/reportData'
import {REPORT_DEFINITIONS} from './shared/reportDefinitions'
import {PageFrame} from './shared/ReportPage.styles'
import ReportTable from './shared/ReportTable'
import {useReportData} from './shared/useReportData'

type Props = {type: ReportType; className?: string}
// A report switch starts a new session, including requests, tabs and filter drafts.
export default function ReportPage(props: Props) {
  return <ReportSession key={props.type} {...props} />
}

function ReportSession({type, className = ''}: Props) {
  const definition = REPORT_DEFINITIONS[type]
  const report = useReportData(type)
  const [filterModal, setFilterModal] = useState(true)
  const [activeView, setActiveView] = useState(definition.tabs[0]?.id || 'principal')
  const dataset = report.datasets?.[activeView]
  const [downloadModal, setDownloadModal] = useState(false)
  const [downloadError, setDownloadError] = useState(false)
  const [downloadURL, setDownloadURL] = useState('')
  const [jobId, setJobId] = useState<string | undefined>()
  const exportRequest = useRef(0)
  const [manual, setManual] = useState<{path: string} | null>(null)
  const [manualModal, setManualModal] = useState(false)
  const [viewerModal, setViewerModal] = useState(false)
  const superAdmin = isSuperAdmin()
  const hasRows = Object.values(report.datasets || {}).some((item) => item.rows.length > 0)
  const exportCounter = Math.max(
    0,
    ...Object.values(report.datasets || {}).map((item) => item.total ?? item.rows.length)
  )

  useEffect(() => {
    if (manualModal) return
    let active = true
    API.getManualReport(definition.apiType)
      .then((response) => {
        if (active) setManual(response?.success ? response.data?.[0] ?? null : null)
      })
      .catch(() => {
        if (active) setManual(null)
      })
    return () => {
      active = false
    }
  }, [definition.apiType, manualModal])
  useEffect(
    () => () => {
      exportRequest.current += 1
    },
    []
  )

  const closeDownload = () => {
    exportRequest.current += 1
    setDownloadModal(false)
  }
  const exportXLSX = async () => {
    if (!report.filters || downloadModal || report.loading || !hasRows) return
    const current = ++exportRequest.current
    setJobId(undefined)
    setDownloadURL('')
    setDownloadError(false)
    setDownloadModal(true)
    if (exportCounter > 2000000) return
    try {
      const response = await requestReport(type, report.filters, true, report.limit)
      if (current !== exportRequest.current) return
      if (!response?.success) throw new Error('REPORT_EXPORT_FAILED')
      if (typeof response.link === 'string' && response.link) setDownloadURL(response.link)
      else if (typeof response.data === 'string' || typeof response.data === 'number')
        setJobId(String(response.data))
      else throw new Error('REPORT_EXPORT_FAILED')
    } catch {
      if (current === exportRequest.current) setDownloadError(true)
    }
  }

  return (
    <PageFrame className={className}>
      <PageTitle>{definition.title}</PageTitle>
      <ReportFiltersModal
        type={type}
        show={filterModal}
        data={report.filters}
        onClose={() => setFilterModal(false)}
        onChange={(next) => {
          setFilterModal(false)
          report.applyFilters(next)
        }}
      />
      <Modal show={downloadModal} onHide={closeDownload} centered dialogClassName='mw-500px'>
        {downloadModal && (
          <DownloadModal
            url={downloadURL}
            jobId={jobId}
            counter={exportCounter}
            error={downloadError}
            onClose={closeDownload}
          />
        )}
      </Modal>
      <Modal
        show={manualModal}
        onHide={() => setManualModal(false)}
        centered
        dialogClassName='mw-800px'
      >
        {manualModal && (
          <ManualModal
            type={definition.apiType}
            item={manual}
            onClose={() => setManualModal(false)}
          />
        )}
      </Modal>
      <Modal
        show={viewerModal}
        onHide={() => setViewerModal(false)}
        centered
        dialogClassName='mw-1000px'
      >
        {viewerModal && manual && (
          <ViewerModal path={manual.path} onClose={() => setViewerModal(false)} />
        )}
      </Modal>
      <section className='report-card' aria-label={`Relatório de ${definition.title}`}>
        <header className='report-heading'>
          <div className='report-title'>
            <span className='report-title-icon'>
              <FileBarChart2 size={23} aria-hidden='true' />
            </span>
            <div>
              <h1>Relatório de {definition.title}</h1>
              <p className='report-caption'>Consulte, organize e exporte seus resultados.</p>
            </div>
          </div>
          <div className='report-actions'>
            <div className='report-limit'>
              <label htmlFor={`${type}-limit`}>Limite:</label>
              <select
                id={`${type}-limit`}
                value={report.limit}
                disabled={report.loading}
                onChange={(event) => report.changeLimit(Number(event.target.value))}
                className='form-select form-select-sm'
              >
                {[100, 250, 500, 1000].map((limit) => (
                  <option key={limit} value={limit}>
                    {limit}
                  </option>
                ))}
              </select>
            </div>
            <button
              type='button'
              className='btn btn-light-primary'
              onClick={() => setFilterModal(true)}
            >
              <Filter size={16} aria-hidden='true' /> Filtros
            </button>
            <button
              type='button'
              className='btn btn-primary'
              disabled={report.loading || !hasRows || downloadModal || report.error}
              onClick={exportXLSX}
            >
              <FileSpreadsheet size={16} aria-hidden='true' /> Exportar XLSX
            </button>
            {(superAdmin || manual) && (
              <div className={`help-button ${manual ? '' : 'missing'}`} title='Manual do relatório'>
                <BtnHelp
                  onClick={() => (superAdmin ? setManualModal(true) : setViewerModal(true))}
                />
              </div>
            )}
          </div>
        </header>
        <div className='report-body' aria-busy={report.loading}>
          {dataset && !report.loading && !report.error && (
            <div className='report-summary'>
              {dataset.total !== null && (
                <span>
                  Total de resultados: <strong>{dataset.total.toLocaleString('pt-BR')}</strong>
                </span>
              )}
              <span>
                Resultados visíveis: <strong>{dataset.rows.length.toLocaleString('pt-BR')}</strong>
              </span>
            </div>
          )}
          {definition.tabs.length > 0 && report.datasets && (
            <div className='report-tabs' role='tablist' aria-label='Visões do relatório'>
              {definition.tabs.map((tab) => (
                <button
                  key={tab.id}
                  type='button'
                  role='tab'
                  id={`${type}-tab-${tab.id}`}
                  aria-selected={activeView === tab.id}
                  aria-controls={`${type}-panel`}
                  onClick={() => setActiveView(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          {report.loading && (
            <div className='report-state' role='status'>
              <span className='spinner-border text-primary' aria-hidden='true' />
              <h2>Processando relatório</h2>
              <p>Aguarde enquanto buscamos os resultados.</p>
            </div>
          )}
          {report.error && (
            <div className='alert alert-danger report-error' role='alert'>
              <AlertCircle size={19} aria-hidden='true' />
              <span>Não foi possível carregar o relatório. Tente novamente.</span>
              <button type='button' className='btn btn-sm btn-light-danger' onClick={report.reload}>
                Tentar novamente
              </button>
            </div>
          )}
          {!report.filters && !report.loading && (
            <div className='report-state'>
              <FileBarChart2 size={38} aria-hidden='true' />
              <p>Selecione os filtros para consultar o relatório.</p>
              <button
                type='button'
                className='btn btn-light-primary btn-sm'
                onClick={() => setFilterModal(true)}
              >
                Definir filtros
              </button>
            </div>
          )}
          {dataset && !report.loading && !report.error && (
            <div
              id={`${type}-panel`}
              role={definition.tabs.length ? 'tabpanel' : undefined}
              aria-labelledby={definition.tabs.length ? `${type}-tab-${activeView}` : undefined}
            >
              {dataset.rows.length ? (
                <ReportTable
                  key={`${type}:${activeView}`}
                  type={type}
                  title={definition.title}
                  dataset={dataset}
                  viewId={activeView}
                />
              ) : (
                <div className='report-state'>
                  <SearchX size={36} aria-hidden='true' />
                  <p>Nenhum resultado encontrado.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </PageFrame>
  )
}
